import React, { useContext, useMemo } from 'react';
import {
    Redirect,
    Route,
    Switch,
    useLocation,
    matchPath,
} from 'react-router-dom';
import DocumentTitle from 'react-document-title';

import Home from './Home/Home';
import GroupsPage from './Groups/Groups';
import MyDocumentsPage from './MyDocuments/MyDocuments';
import ProjectPage from './Project/Project';
import DocumentTypePage from './DocumentType/DocumentType';
import ErrorPage from './Error/404';
import Error2Page from './Error/500';
import DocumentsLibraryPopups from '../containers/DocumentsLibraryPopups/DocumentsLibraryPopups';
import TrendingNow from "@pages/TrendingNow/TrendingNow";
import InstantCharts from './InstantCharts/InstantCharts';
import PackagesPage from './PackagesPage/PackagesPage';
import GlobalNotification from '../components/GlobalNotification/GlobalNotification';

import getQueryParam from '../core/getQueryParam';
import ChangePassNotification from '../components/ChangePassNotification/ChangePassNotification';
import ChainPage from "@pages/ChainPage/ChainPage";
import { routeNames } from "@core/routeNames";
import FireflyChains from "./NewTopChains/FireflyChains";
import Places from "./NewTopChains/Places";
import { AppContext } from "../AppContext";
import Documents from './NewDocuments/Documents/Documents';
import DocumentView from './NewDocuments/DocumentView/DocumentView';
import FoodProfile from "@pages/Food/Food";
import CategoryProfile from "@pages/CategoryProfile/CategoryProfile";
import TestPage from './TestPage';
import {
    cssX,
    GtmInit,
    PlatformNavigation as PlatformNavigationComponent,
} from "@datassential/platform-ui-lib";
import SearchTabs from "@pages/SearchTabs/SearchTabs";
import PackagesModals from "@containers/PageHeader/PackagesModals";
import WorkspaceDirect from "@pages/Home/WorkspaceDirect";

const AppPage = (options) => {
    return (props) => (
        <>
            <DocumentTitle title={options.pageTitle || 'Datassential One'}/>
            <options.component {...props} />
        </>
    );
};

const PlatformNavigation = () => {
    const location = useLocation();

    const isHomeRoute = useMemo(
        () => matchPath(location.pathname, routeNames.home).isExact,
        [location.pathname]
    );

    return (
        <div className={cssX('-use-rem')}>
            <PlatformNavigationComponent isSearchDecoratedAsInput={isHomeRoute} />
        </div>
    );
};

function RouterComponent() {
    const {
        globalVariables: { hasFFChainsEnableLoginPermission }
    } = useContext(AppContext);
    const isFFChainsAllowed = hasFFChainsEnableLoginPermission;

    return (
        <Route render={({ location }) =>
            <div>
                    <PlatformNavigation/>
                    {process.env.NODE_ENV !== 'development' && <GtmInit/>}
                    <DocumentsLibraryPopups/>
                    <ChangePassNotification/>
                    <PackagesModals/>
                    <GlobalNotification/>
                    <Switch>
                        <Route exact path="/test" component={TestPage}/>
                        <Route
                            exact path={routeNames.home}
                            component={AppPage({
                                component: Home,
                                animationClass: 'route-change-slide'
                            })}
                        />
                        <Route
                            exact path={routeNames.workspace}
                            component={AppPage({
                                component: WorkspaceDirect,
                                animationClass: 'route-change-slide'
                            })}
                        />
                        <Route
                            exact path={routeNames.search}
                            component={AppPage({
                                component: SearchTabs,
                            })}
                        />
                        <Route
                            path={routeNames.food}
                            component={AppPage({
                                component: FoodProfile,
                                animationClass: 'route-change',
                                pageTitle: `
                                        Datassential One | Results for ${getQueryParam(location.search, 'q')}
                                    `
                            })}
                        />
                        <Route
                            path={routeNames.categoryProfile}
                            component={AppPage({
                                component: CategoryProfile,
                                pageTitle: 'Datassential One | Category Profile'
                            })}
                        />
                        {/* Retired page — redirects legacy `?q=` / `?tabIndex=2` links to `chains`. */}
                        <Route
                            path={routeNames.places}
                            component={Places}
                        />
                        <Route
                            path={routeNames.documents}
                            component={AppPage({
                                component: Documents,
                                animationClass: 'route-change',
                                pageTitle: `
                                        Datassential One | Results for ${getQueryParam(location.search, 'q')}
                                    `
                            })}
                        />
                        <Route
                            path={routeNames.myDocuments}
                            component={AppPage({
                                component: MyDocumentsPage,
                                animationClass: 'route-change',
                                pageTitle: `
                                        Datassential One | Documents library
                                `
                            })}
                        />
                        <Route
                            path={routeNames.projects}
                            component={AppPage({
                                component: ProjectPage,
                                animationClass: 'route-change',
                                pageTitle: `
                                        Datassential One | Results for ${getQueryParam(location.search, 'q')}
                                    `
                            })}
                        />
                        <Route
                            path={routeNames.documentType}
                            component={AppPage({
                                component: DocumentTypePage,
                                animationClass: 'route-change',
                                pageTitle: `
                                        Datassential One | Results for ${getQueryParam(location.search, 'q')}
                                    `
                            })}
                        />
                        <Route
                            path={routeNames.viewDocument}
                            component={AppPage({
                                component: DocumentView,
                                animationClass: 'route-change'
                            })}
                        />
                        <Route
                            path={routeNames.fireflyChains}
                            render={({ location }) => (
                                <Redirect
                                    to={{
                                        pathname: routeNames.chains,
                                        search: location.search,
                                        hash: location.hash,
                                    }}
                                />
                            )}
                        />
                        {isFFChainsAllowed && (
                            <Route
                                path={routeNames.chains}
                                component={AppPage({
                                    component: FireflyChains,
                                    animationClass: 'route-change'
                                })}
                            />
                        )}
                        <Route
                            path={routeNames.chainDetails}
                            component={AppPage({
                                component: ChainPage,
                                animationClass: 'route-change'
                            })}
                        />
                        <Route
                            path={routeNames.allMacs}
                            render={({ location }) => (
                                <Redirect
                                    to={{
                                        pathname: routeNames.trendingNow,
                                        search: location.search,
                                        hash: location.hash,
                                    }}
                                />
                            )}
                        />
                        <Route
                            path={routeNames.trendingNow}
                            component={AppPage({
                                component: TrendingNow,
                                animationClass: 'route-change',
                                pageTitle: `
                                    Trending Now by Datassential
                                `
                            })}
                        />
                        <Route
                            path={routeNames.instantCharts}
                            component={AppPage({
                                component: InstantCharts,
                                animationClass: 'route-change'
                            })}
                        />
                        <Route
                            path={routeNames.packages}
                            component={AppPage({
                                component: PackagesPage,
                                animationClass: 'route-change'
                            })}
                        />
                        <Route
                            path={routeNames.groups}
                            component={AppPage({
                                component: GroupsPage,
                                animationClass: 'route-change'
                            })}
                        />
                        <Route
                            path={routeNames.error}
                            component={ErrorPage}
                        />
                        <Route
                            path={routeNames.error2}
                            component={Error2Page}
                        />
                        <Redirect to="/"/>
                    </Switch>
                </div>
            }/>
    );
}

export default RouterComponent;
